import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectorOverlay } from '../common';
import { destinationService, addressSearchService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/**
 * 골프장 선택 — SelectorOverlay 기반
 */
export default function GolfCourseSelector({ value, onChange, placeholder, className = '', variant = 'default' }) {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t('form.select_golf_course');
  const regionSidebar = [
    { id: 'all', label: t('common.all_region') },
    { id: '경기', label: t('common.region_gyeonggi') },
    { id: '인천', label: t('common.region_incheon') },
    { id: '강원', label: t('common.region_gangwon') },
  ];
  const regionLabels = {
    '경기': t('common.region_gyeonggi'),
    '인천': t('common.region_incheon'),
    '강원': t('common.region_gangwon'),
    '서울': t('common.region_seoul'),
  };
  const [open, setOpen] = useState(false);
  const [activeRegion, setActiveRegion] = useState('all');
  const [search, setSearch] = useState('');
  const [rawCourses, setRawCourses] = useState([]);
  // 추천 OFF 골프장 — 기본 화면(지역 그룹/사이드바)엔 미노출, 검색어 입력 시에만 결과에 합류.
  // 스키 selector 와 동일 패턴. 어드민 등록된 모든 코스가 검색은 가능하도록.
  const [unrecommendedCourses, setUnrecommendedCourses] = useState([]);

  useEffect(() => {
    let cancelled = false;
    destinationService.getGolfCourses().then((data) => {
      const all = data || [];
      const recommended = all.filter((g) => g.is_recommended === true);
      const unrec = all.filter((g) => g.is_recommended === false);
      if (cancelled) return;
      setRawCourses(recommended.map((g) => ({ value: String(g.id), label: g.name, region: g.region, address: g.address })));
      setUnrecommendedCourses(unrec.map((g) => ({ value: String(g.id), label: g.name, region: g.region, address: g.address })));
    }).catch((e) => console.warn(e.message));
    return () => { cancelled = true; };
  }, []);

  // Row 30: 골프장 라벨/주소 render-time 번역 (AirportSelector와 동일 패턴 — 재시도·캐시 안정성)
  const labelBundle = useMemo(() => rawCourses.map((c) => c.label), [rawCourses]);
  const addressBundle = useMemo(() => rawCourses.map((c) => c.address || ''), [rawCourses]);
  const tLabels = useDeepLTranslated(labelBundle);
  const tAddresses = useDeepLTranslated(addressBundle);
  const allCourses = useMemo(
    () => rawCourses.map((c, i) => ({
      ...c,
      nameKo: c.label, // #217: BE Golf.name 매칭용 원본 한글명 보존 (label 은 표시용 번역본)
      label: tLabels[i] || c.label,
      address: tAddresses[i] || c.address,
    })),
    [rawCourses, tLabels, tAddresses]
  );

  // 추천 OFF 코스 번역 — 검색 결과 합류용
  const unrecLabelBundle = useMemo(() => unrecommendedCourses.map((c) => c.label), [unrecommendedCourses]);
  const unrecAddressBundle = useMemo(() => unrecommendedCourses.map((c) => c.address || ''), [unrecommendedCourses]);
  const tUnrecLabels = useDeepLTranslated(unrecLabelBundle);
  const tUnrecAddresses = useDeepLTranslated(unrecAddressBundle);
  const unrecCoursesTranslated = useMemo(
    () => unrecommendedCourses.map((c, i) => ({
      ...c,
      nameKo: c.label, // #217: BE 매칭용 원본 한글명 보존
      label: tUnrecLabels[i] || c.label,
      address: tUnrecAddresses[i] || c.address,
    })),
    [unrecommendedCourses, tUnrecLabels, tUnrecAddresses]
  );

  const displayLabel = useMemo(() => {
    if (!value) return null;
    // #169: 추천 코스(allCourses)뿐 아니라 검색 합류(추천 OFF) 코스도 함께 조회.
    //       못 찾아도 raw id(value)를 절대 노출하지 않음(null → placeholder).
    const found = [...allCourses, ...unrecCoursesTranslated].find((c) => c.value === value);
    return found ? found.label : null;
  }, [value, allCourses, unrecCoursesTranslated]);

  const groupedByRegion = useMemo(() => {
    const regionOrder = ['경기', '인천', '강원'];
    const groups = [];
    regionOrder.forEach((key) => {
      const courses = allCourses.filter((c) => c.region === key);
      if (courses.length > 0) {
        groups.push({ key, label: key, courses });
      }
    });
    return groups;
  }, [allCourses]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (q) {
      // 검색어 입력 시 추천 ON + OFF 모두 합류 (스키 selector 와 동일 패턴).
      // 어드민에 등록된 모든 골프장이 검색 가능해야 한다는 클라 요구.
      const pool = [...allCourses, ...unrecCoursesTranslated];
      const matched = pool.filter((c) => c.label.toLowerCase().includes(q));
      matched.sort((a, b) => {
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        return aStarts - bStarts;
      });
      return { mode: 'search', results: matched };
    }

    if (activeRegion === 'all') {
      return { mode: 'grouped', groups: groupedByRegion };
    }

    const courses = allCourses.filter((c) => c.region === activeRegion);
    return {
      mode: 'single',
      label: activeRegion,
      courses,
    };
  }, [search, activeRegion, allCourses, unrecCoursesTranslated, groupedByRegion]);

  const handleOpen = () => {
    if (!open) {
      setSearch('');
      setActiveRegion('all');
    }
    setOpen((v) => !v);
  };

  const handleSelect = async (course) => {
    const regionKey = course.region || activeRegion;
    const label = course.label || course.value;
    // #217: 5번째 인자 nameKo(원본 한글명) 전달 → BE Golf.name/Ski 와 동일 규칙으로 가격 매칭.
    //   label(번역본)은 표시 전용. 카카오 주소 조회도 한글명으로 해야 적중률이 높음.
    const nameKo = course.nameKo || course.label || course.value;
    // DB 골프장에 address 없으면 카카오 API로 실제 주소 자동 조회
    if (course.address) {
      onChange(course.value, regionKey, label, course.address, nameKo);
    } else {
      onChange(course.value, regionKey, label, '', nameKo); // 즉시 이름으로 먼저 반영
      try {
        const results = await addressSearchService.search(nameKo);
        const match = results.find((r) => r.name.includes(nameKo.replace(/C\.C\.|CC|GC|컨트리클럽/g, '').trim().slice(0, 4)));
        if (match) onChange(course.value, regionKey, label, match.address, nameKo);
      } catch {}
    }
    setOpen(false);
    setSearch('');
  };

  const renderCourseItem = (course) => {
    const isSelected = course.value === value;
    return (
      <button
        key={course.value}
        type="button"
        onClick={() => handleSelect(course)}
        className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isSelected
            ? 'text-orange-500 font-semibold bg-orange-50'
            : 'text-mono-700 hover:bg-mono-50 hover:text-mono-900'
        }`}
      >
        {course.label}
      </button>
    );
  };

  return (
    <SelectorOverlay
      displayLabel={displayLabel}
      placeholder={effectivePlaceholder}
      className={className}
      variant={variant}
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      maxWidth="780px"
      search={search}
      onSearchChange={(e) => setSearch(e.target.value)}
      searchPlaceholder={window.innerWidth < 640 ? t('form.select_golf_course') : t('common.search_golf_long')}
      title={t('common.main_golf_quick')}
    >
      {/* Mobile: 가로 탭 + 콘텐츠 / PC: 세로 사이드바 + 콘텐츠 */}
      <div className="flex flex-col sm:flex-row h-full" style={{ minHeight: '240px', maxHeight: window.innerWidth >= 640 ? 'calc(80vh - 160px)' : undefined }}>
        {/* Mobile: 가로 탭 */}
        {!search.trim() && (
          <div className="sm:hidden flex border-b border-mono-100 overflow-x-auto scrollbar-hide px-3 py-1.5 gap-1.5 shrink-0">
            {regionSidebar.map((region) => {
              const isActive = region.id === activeRegion;
              return (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => setActiveRegion(region.id)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    isActive
                      ? 'bg-orange-500 text-white'
                      : 'bg-mono-100 text-mono-500'
                  }`}
                >
                  {region.label}
                </button>
              );
            })}
          </div>
        )}
        {/* PC: 세로 사이드바 */}
        {!search.trim() && (
          <div className="hidden sm:block w-[120px] flex-shrink-0 bg-mono-50 border-r border-mono-100 overflow-y-auto overscroll-contain py-2">
            {regionSidebar.map((region) => {
              const isActive = region.id === activeRegion;
              return (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => setActiveRegion(region.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'text-orange-500 font-bold border-l-2 border-orange-500 bg-white'
                      : 'text-mono-500 hover:text-mono-700 border-l-2 border-transparent'
                  }`}
                >
                  {region.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5">
          {filteredData.mode === 'search' && (
            <>
              {filteredData.results.length > 0 && (
                <div className="mb-6">
                  <div className="text-xs font-bold text-mono-400 mb-3">{t('common.main_golf_courses')} ({filteredData.results.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                    {filteredData.results.map(renderCourseItem)}
                  </div>
                </div>
              )}

              {filteredData.results.length === 0 && (
                <p className="text-sm text-mono-400 text-center py-8">{t('common.no_search_results')}</p>
              )}
            </>
          )}

          {filteredData.mode === 'grouped' && (
            filteredData.groups.map((group) => (
              <div key={group.key} className="mb-6 last:mb-0">
                <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                  {regionLabels[group.label] || group.label}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                  {group.courses.map(renderCourseItem)}
                </div>
              </div>
            ))
          )}

          {filteredData.mode === 'single' && (
            <>
              <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                {regionLabels[filteredData.label] || filteredData.label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                {filteredData.courses.map(renderCourseItem)}
              </div>
            </>
          )}
        </div>
      </div>
    </SelectorOverlay>
  );
}
